import React, { useContext, useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { useIntl } from 'react-intl';

import { Colors } from 'app/shared/theme';
import { AlertContext } from 'app/shared/context';
import {
  AuthFormContextProvider,
  MultiStepSignUpContextProvider,
} from 'app/shared/context';
import { useEventPageContext } from 'app/fan/context/EventPage';
import { ExpressCheckoutContextProvider } from 'app/fan/context/ExpressCheckout';
import { StripeElementsProvider } from 'app/fan/context/StripeElementsProvider';
import {
  ApplyFlowPreInviteContainer,
  BuyFlowPrePurchaseContainer,
  CompetitionFlowPreWinContainer,
} from 'app/fan/components/pages/Event/EventPages';

import { PlusOneOverlay, PlusOneStatus } from './PlusOneOverlay';

export const PlusOneEventPage = () => {
  const { formatMessage } = useIntl();
  const { event, eventAttendee } = useEventPageContext();

  const successMessage = formatMessage({
    id: 'event.plusOneFlow.snackbar.success',
  });

  // NOTE: Reloading the session when a user completes the 'complete signup' form causes
  // everything inside the AuthContextProvider to rerender, which means this component
  // initializes itself all over again. To get around this, we are setting a cookie on success.
  const { completed_plus_one_flow_signup: completedPlusOneFlowSignup } =
    Cookies.get();
  const initialState =
    completedPlusOneFlowSignup === 'true'
      ? {
          plusOneStatus: 'success' as PlusOneStatus,
          isSnackbarOpen: true,
          snackbarMessage: successMessage,
          isPlusOneModalOpen: false,
        }
      : {
          plusOneStatus: null,
          isSnackbarOpen: false,
          snackbarMessage: null,
          isPlusOneModalOpen: true,
        };

  const [plusOneStatus, setPlusOneStatus] = useState<PlusOneStatus>(
    initialState.plusOneStatus
  );
  const alertContext = useContext(AlertContext);

  useEffect(() => {
    if (initialState.isSnackbarOpen && initialState.snackbarMessage) {
      alertContext.addAlert({
        message: initialState.snackbarMessage,
        type: initialState.plusOneStatus as keyof Colors['messaging'],
        id: 'plus-one-snackbar',
      });
    }
  }, [initialState]);

  const [isPlusOneModalOpen, setIsPlusOneModalOpen] = useState(
    initialState.isPlusOneModalOpen
  );

  useEffect(() => {
    if (plusOneStatus === 'success') {
      alertContext.addAlert({
        message: successMessage,
        type: plusOneStatus,
        id: 'plus-one-snackbar',
      });
    } else if (plusOneStatus === 'error') {
      alertContext.addAlert({
        message: formatMessage({ id: 'event.plusOneFlow.snackbar.error' }),
        type: plusOneStatus,
        id: 'plus-one-snackbar',
      });
    }
  }, [plusOneStatus]);

  // We will never run into this scenario, but include this code so that
  // Typescript knows to expect these to be present
  if (!event || !eventAttendee) {
    return null;
  }

  const renderMainContent = () => {
    if (event.competition?.id && eventAttendee.source === 'competition') {
      return (
        <CompetitionFlowPreWinContainer data-qaid="event-page-competition-flow-plus-one" />
      );
    }

    if (event.attendeeFlow === 'apply') {
      return (
        <ApplyFlowPreInviteContainer data-qaid="event-page-apply-flow-plus-one" />
      );
    }

    return (
      <StripeElementsProvider eventId={event.id}>
        <ExpressCheckoutContextProvider
          event={event}
          eventAttendee={eventAttendee}
        >
          <BuyFlowPrePurchaseContainer data-qaid="event-page-buy-flow-plus-one" />
        </ExpressCheckoutContextProvider>
      </StripeElementsProvider>
    );
  };

  return (
    <AuthFormContextProvider
      isCompleteSignUpFlow={true}
      isPlusOneFlow={true}
      isCheckoutAuth={false}
    >
      <MultiStepSignUpContextProvider>
        <PlusOneOverlay
          plusOneStatus={plusOneStatus}
          setPlusOneStatus={setPlusOneStatus}
          isPlusOneModalOpen={isPlusOneModalOpen}
          setIsPlusOneModalOpen={setIsPlusOneModalOpen}
        />
        {renderMainContent()}
      </MultiStepSignUpContextProvider>
    </AuthFormContextProvider>
  );
};
